/**
 * CotacaoEditDialog - Modal wizard de criação/edição de cotação (3 steps) — extraído de Compras.jsx.
 * Componente de apresentação: todo o estado e handlers vêm via props (Compras.jsx segue orquestrador).
 */
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { CurrencyInput } from '../ui/currency-input';
import {
  ChevronRight, ChevronLeft, Check, Search, X, Plus,
  Package, Truck, DollarSign, FileText, Trash2, Users, Paperclip,
} from 'lucide-react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import AnexosManager from '../AnexosManager';
import AnexosErrorBoundary from '../AnexosErrorBoundary';
import { getFornecedorNome } from './utils';
import MoedaSelect from './MoedaSelect';
import { INCOTERMS } from '../../lib/tradeConstants';

const CotacaoEditDialog = ({
  open,
  onClose,
  editingCotacao,
  cotacaoForm,
  setCotacaoForm,
  cotacaoStep,
  empresas,
  projetos,
  produtos,
  tiposServico,
  tiposServicoFiltrados,
  fornecedores,
  aprovadores,
  aprovadorSearch,
  setAprovadorSearch,
  fornSearchCot,
  setFornSearchCot,
  anexosCotacaoForm,
  anexosPendentes,
  setAnexosPendentes,
  submitLoading,
  onAvancar,
  onVoltar,
  onToggleAprovador,
  onAddItem,
  onUpdateItem,
  onRemoveItem,
  onToggleFornecedor,
  onCadastrarProduto,
  onCadastrarFornecedor,
  onUploadAnexo,
  onDeleteAnexo,
  onPatchAnexoForm,
  onSave,
}) => {
  const requestClose = () => {
    const hasData = cotacaoForm.titulo || cotacaoForm.itens?.length > 0 || cotacaoForm.fornecedores?.length > 0;
    if (hasData && !window.confirm('Tem certeza? Os dados não salvos serão perdidos.')) return;
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) requestClose(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>{editingCotacao ? 'Editar Cotação' : 'Nova Cotação'}</span>
            <div className="flex items-center gap-2 text-sm font-normal">
              <span className={`px-2 py-1 rounded ${cotacaoStep >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>1. Informações</span>
              <ChevronRight className="w-4 h-4 text-gray-400" />
              <span className={`px-2 py-1 rounded ${cotacaoStep >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>2. Itens</span>
              <ChevronRight className="w-4 h-4 text-gray-400" />
              <span className={`px-2 py-1 rounded ${cotacaoStep >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>3. Fornecedores</span>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Step 1: Informações */}
        {cotacaoStep === 1 && (
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Empresa *</Label>
                <Select
                  value={cotacaoForm.empresa_id}
                  onValueChange={(v) => setCotacaoForm(prev => ({ ...prev, empresa_id: v }))}
                >
                  <SelectTrigger data-testid="select-empresa">
                    <SelectValue placeholder="Selecione a empresa" />
                  </SelectTrigger>
                  <SelectContent>
                    {empresas.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.nome_fantasia || emp.razao_social}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Projeto (opcional)</Label>
                <Select
                  value={cotacaoForm.projeto_id || "none"}
                  onValueChange={(v) => {
                    const projeto = projetos.find(p => p.id === v);
                    setCotacaoForm(prev => ({
                      ...prev,
                      projeto_id: v === "none" ? '' : v,
                      projeto_nome: projeto?.name || projeto?.nome || ''
                    }));
                  }}
                >
                  <SelectTrigger data-testid="select-projeto">
                    <SelectValue placeholder="Vincular a um projeto" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhum projeto</SelectItem>
                    {projetos.map(proj => (
                      <SelectItem key={proj.id} value={proj.id}>
                        {proj.name || proj.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Título da Cotação *</Label>
                <Input
                  value={cotacaoForm.titulo}
                  onChange={(e) => setCotacaoForm(prev => ({ ...prev, titulo: e.target.value }))}
                  placeholder="Ex: Cotação Motores Mercury 150HP"
                  data-testid="input-titulo"
                />
              </div>
              <div>
                <Label>Data Limite de Resposta</Label>
                <Input
                  type="date"
                  value={cotacaoForm.data_limite_resposta}
                  onChange={(e) => setCotacaoForm(prev => ({ ...prev, data_limite_resposta: e.target.value }))}
                  data-testid="input-data-limite"
                />
              </div>
            </div>

            <div>
              <Label>Moeda da Cotação *</Label>
              <MoedaSelect
                value={cotacaoForm.moeda || 'BRL'}
                onChange={(v) => setCotacaoForm(prev => ({ ...prev, moeda: v }))}
                className="w-full"
                testId="cotacao-moeda-select"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Todos os valores desta cotação (itens, descontos, impostos, frete) serão nesta moeda.
              </p>
            </div>

            {cotacaoForm.moeda && cotacaoForm.moeda !== 'BRL' && (
              <div>
                <Label>Taxa de câmbio (gerencial) — 1 {cotacaoForm.moeda} = ? R$</Label>
                <Input
                  type="number"
                  step="0.0001"
                  min="0"
                  value={cotacaoForm.taxa_cambio ?? ''}
                  onChange={(e) => setCotacaoForm(prev => ({ ...prev, taxa_cambio: e.target.value === '' ? '' : parseFloat(e.target.value) }))}
                  placeholder="Ex: 5.30"
                  data-testid="cotacao-taxa-cambio"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Opcional. Não altera os valores — usada apenas para exibir o equivalente em R$ e nos totais gerenciais. O pedido gerado herda esta taxa.
                </p>
              </div>
            )}

            {/* Condições de importação / entrega */}
            <div className="border-t pt-3 mt-1">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Condições de importação / entrega</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Incoterm</Label>
                  <Select
                    value={cotacaoForm.incoterm || 'none'}
                    onValueChange={(v) => setCotacaoForm(prev => ({ ...prev, incoterm: v === 'none' ? '' : v }))}
                  >
                    <SelectTrigger data-testid="cotacao-incoterm">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Nenhum —</SelectItem>
                      {INCOTERMS.map(i => (
                        <SelectItem key={i.code} value={i.code}>{i.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Validade da proposta</Label>
                  <Input
                    type="date"
                    value={cotacaoForm.validade_proposta || ''}
                    onChange={(e) => setCotacaoForm(prev => ({ ...prev, validade_proposta: e.target.value }))}
                    data-testid="cotacao-validade-proposta"
                  />
                </div>
                <div>
                  <Label>Lead Time (dias)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={cotacaoForm.lead_time_dias ?? ''}
                    onChange={(e) => setCotacaoForm(prev => ({ ...prev, lead_time_dias: e.target.value === '' ? '' : parseInt(e.target.value) }))}
                    placeholder="Fabricação"
                    data-testid="cotacao-lead-time"
                  />
                </div>
                <div>
                  <Label>Transit Time (dias)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={cotacaoForm.transit_time_dias ?? ''}
                    onChange={(e) => setCotacaoForm(prev => ({ ...prev, transit_time_dias: e.target.value === '' ? '' : parseInt(e.target.value) }))}
                    placeholder="Trânsito"
                    data-testid="cotacao-transit-time"
                  />
                </div>
                {cotacaoForm.moeda && cotacaoForm.moeda !== 'BRL' && (
                  <div className="col-span-2">
                    <Label>Porto/Aeroporto de origem</Label>
                    <Input
                      value={cotacaoForm.porto_origem || ''}
                      onChange={(e) => setCotacaoForm(prev => ({ ...prev, porto_origem: e.target.value }))}
                      placeholder="Ex: Shanghai (CNSHA)"
                      data-testid="cotacao-porto-origem"
                    />
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label>Descrição</Label>
              <div className="quill-compact" data-testid="input-descricao">
                <ReactQuill
                  theme="snow"
                  value={cotacaoForm.descricao}
                  onChange={(value) => setCotacaoForm(prev => ({ ...prev, descricao: value }))}
                  placeholder="Descreva os requisitos da cotação..."
                  modules={{
                    toolbar: [
                      ['bold', 'italic', 'underline'],
                      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                      ['clean']
                    ]
                  }}
                  formats={['bold', 'italic', 'underline', 'list']}
                  style={{ minHeight: '100px' }}
                />
              </div>
            </div>

            <div>
              <Label>Observações</Label>
              <Textarea
                value={cotacaoForm.observacao}
                onChange={(e) => setCotacaoForm(prev => ({ ...prev, observacao: e.target.value }))}
                placeholder="Observações adicionais..."
                rows={2}
              />
            </div>

            {/* Checkbox Urgente */}
            <div className="flex items-center space-x-3 p-3 bg-red-50 border border-red-200 rounded-lg">
              <input
                type="checkbox"
                id="cotacao-urgente"
                checked={cotacaoForm.urgente}
                onChange={(e) => setCotacaoForm(prev => ({ ...prev, urgente: e.target.checked }))}
                className="w-5 h-5 text-red-600 border-red-300 rounded focus:ring-red-500"
              />
              <Label htmlFor="cotacao-urgente" className="cursor-pointer flex items-center gap-2">
                <span className="text-red-700 font-medium">🚨 Marcar como URGENTE</span>
                <span className="text-red-600 text-sm">(Prioridade alta)</span>
              </Label>
            </div>

            {/* Responsáveis pela Aprovação */}
            <div className="border-t pt-4">
              <Label className="text-base font-semibold flex items-center gap-2">
                <Check className="w-4 h-4 text-emerald-600" />
                Responsáveis pela Aprovação
              </Label>
              <p className="text-xs text-gray-500 mt-1 mb-2">
                Defina quem poderá aprovar esta cotação (cada aprovador tem um limite financeiro próprio).
                Cotações sem responsáveis seguem direto para geração de pedidos.
              </p>

              {aprovadores.length === 0 ? (
                <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                  Nenhum usuário com a feature <code>compras_aprovar</code> encontrado.
                  Configure perfis em <strong>Configurações → Perfis</strong> para habilitar aprovações.
                </div>
              ) : (
                <>
                  {/* Chips de aprovadores selecionados */}
                  {(cotacaoForm.responsaveis_aprovacao_ids || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2" data-testid="chips-aprovadores">
                      {(cotacaoForm.responsaveis_aprovacao_ids || []).map(id => {
                        const a = aprovadores.find(x => x.id === id);
                        if (!a) return null;
                        const limStr = a.limite_aprovacao_brl > 0
                          ? `R$ ${a.limite_aprovacao_brl.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                          : 'sem limite';
                        return (
                          <Badge
                            key={id}
                            variant="secondary"
                            className="bg-emerald-100 text-emerald-800 border border-emerald-300 pl-2 pr-1 py-1 flex items-center gap-1"
                            data-testid={`chip-aprovador-${id}`}
                          >
                            <span className="text-xs">{a.full_name} <span className="text-[10px] opacity-70">({limStr})</span></span>
                            <button
                              type="button"
                              onClick={() => onToggleAprovador(a)}
                              className="hover:bg-emerald-200 rounded-full p-0.5"
                              aria-label="Remover aprovador"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        );
                      })}
                    </div>
                  )}

                  {/* Input de busca */}
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      value={aprovadorSearch}
                      onChange={(e) => setAprovadorSearch(e.target.value)}
                      placeholder="Buscar aprovador por nome ou email..."
                      className="pl-8"
                      data-testid="input-busca-aprovador"
                    />
                  </div>

                  {/* Lista filtrada */}
                  {aprovadorSearch.trim() && (
                    <div className="border rounded mt-1 max-h-40 overflow-y-auto bg-white shadow-sm">
                      {aprovadores
                        .filter(a => {
                          const q = aprovadorSearch.toLowerCase();
                          return (
                            (a.full_name || '').toLowerCase().includes(q) ||
                            (a.email || '').toLowerCase().includes(q)
                          );
                        })
                        .map(a => {
                          const sel = (cotacaoForm.responsaveis_aprovacao_ids || []).includes(a.id);
                          const limStr = a.limite_aprovacao_brl > 0
                            ? `R$ ${a.limite_aprovacao_brl.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                            : 'sem limite definido';
                          return (
                            <button
                              key={a.id}
                              type="button"
                              onClick={() => onToggleAprovador(a)}
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between ${sel ? 'bg-emerald-50' : ''}`}
                              data-testid={`opt-aprovador-${a.id}`}
                            >
                              <div>
                                <div className="font-medium">{a.full_name}</div>
                                <div className="text-xs text-gray-500">{a.email}</div>
                              </div>
                              <div className="text-xs flex items-center gap-1">
                                <span className={a.limite_aprovacao_brl > 0 ? 'text-gray-700' : 'text-amber-600'}>
                                  {limStr}
                                </span>
                                {sel && <Check className="w-4 h-4 text-emerald-600" />}
                              </div>
                            </button>
                          );
                        })}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="flex justify-between pt-4 border-t">
              <Button variant="outline" onClick={requestClose}>
                Cancelar
              </Button>
              <Button onClick={onAvancar} data-testid="btn-avancar-step1">
                Avançar
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Itens */}
        {cotacaoStep === 2 && (
          <div className="space-y-4 mt-4">
            <div className="flex justify-between items-center">
              <Label className="text-base font-medium">Itens da Cotação</Label>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onCadastrarProduto()}
                className="text-blue-600"
              >
                <Plus className="w-4 h-4 mr-1" />
                Cadastrar Produto
              </Button>
            </div>

            {/* Botões de tipo lado a lado */}
            <div className="grid grid-cols-5 gap-2">
              <Button variant="outline" onClick={() => onAddItem('produto')} className="flex flex-col items-center h-auto py-3">
                <Package className="w-5 h-5 mb-1" />
                <span className="text-xs">Produto</span>
              </Button>
              <Button variant="outline" onClick={() => onAddItem('servico')} className="flex flex-col items-center h-auto py-3">
                <Package className="w-5 h-5 mb-1" />
                <span className="text-xs">Serviço</span>
              </Button>
              <Button variant="outline" onClick={() => onAddItem('frete')} className="flex flex-col items-center h-auto py-3">
                <Truck className="w-5 h-5 mb-1" />
                <span className="text-xs">Frete</span>
              </Button>
              <Button variant="outline" onClick={() => onAddItem('reembolso')} className="flex flex-col items-center h-auto py-3">
                <DollarSign className="w-5 h-5 mb-1" />
                <span className="text-xs">Reembolso</span>
              </Button>
              <Button variant="outline" onClick={() => onAddItem('outro')} className="flex flex-col items-center h-auto py-3">
                <FileText className="w-5 h-5 mb-1" />
                <span className="text-xs">Outro</span>
              </Button>
            </div>

            {cotacaoForm.itens.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed rounded-lg">
                <Package className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500">Nenhum item adicionado</p>
                <p className="text-xs text-gray-400 mt-1">
                  Clique em um dos botões acima para adicionar itens
                </p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[40vh] overflow-y-auto">
                {cotacaoForm.itens.map((item, index) => (
                  <Card key={item.id} className="border">
                    <CardContent className="py-3">
                      <div className="flex items-start gap-3">
                        <div className="flex flex-col items-center gap-1">
                          <span className="bg-blue-100 text-blue-700 text-xs font-medium px-2 py-1 rounded">
                            #{index + 1}
                          </span>
                          <Badge variant="outline" className="text-xs capitalize">
                            {item.tipo || 'outro'}
                          </Badge>
                        </div>
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                          {/* Seleção de Produto do Estoque - com busca */}
                          {item.tipo === 'produto' && (
                            <div className="md:col-span-2 relative">
                              <Label className="text-xs">Selecionar Produto (digite para buscar)</Label>
                              <div className="relative">
                                <Input
                                  placeholder="Digite o nome do produto..."
                                  value={item.produtoBusca || item.descricao || ''}
                                  onChange={(e) => {
                                    onUpdateItem(index, 'produtoBusca', e.target.value);
                                    onUpdateItem(index, 'showProdutoList', true);
                                  }}
                                  onFocus={() => onUpdateItem(index, 'showProdutoList', true)}
                                  className="w-full"
                                />
                                {item.showProdutoList && item.produtoBusca && (
                                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                    {produtos
                                      .filter(prod =>
                                        prod.nome?.toLowerCase().includes((item.produtoBusca || '').toLowerCase()) ||
                                        prod.codigo?.toLowerCase().includes((item.produtoBusca || '').toLowerCase()) ||
                                        prod.sku?.toLowerCase().includes((item.produtoBusca || '').toLowerCase())
                                      )
                                      .slice(0, 10)
                                      .map(prod => (
                                        <div
                                          key={prod.id}
                                          className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                                          onClick={() => {
                                            onUpdateItem(index, 'produto_id', prod.id);
                                            onUpdateItem(index, 'descricao', prod.nome);
                                            onUpdateItem(index, 'codigo', prod.codigo || prod.sku || '');
                                            onUpdateItem(index, 'produtoBusca', prod.nome);
                                            onUpdateItem(index, 'showProdutoList', false);
                                            if (prod.marca || prod.marca_nome) onUpdateItem(index, 'marca', prod.marca || prod.marca_nome);
                                            if (prod.modelo) onUpdateItem(index, 'modelo', prod.modelo);
                                            if (prod.unidade) onUpdateItem(index, 'unidade', prod.unidade);
                                            if (prod.categoria) onUpdateItem(index, 'categoria', prod.categoria);
                                          }}
                                        >
                                          <span className="font-medium">{(prod.codigo || prod.sku) ? `[${prod.codigo || prod.sku}] ` : ''}{prod.nome}</span>
                                          {(prod.marca || prod.marca_nome) && <span className="text-gray-500 text-xs ml-2">({prod.marca || prod.marca_nome})</span>}
                                        </div>
                                      ))
                                    }
                                    {produtos.filter(prod =>
                                      prod.nome?.toLowerCase().includes((item.produtoBusca || '').toLowerCase()) ||
                                      prod.codigo?.toLowerCase().includes((item.produtoBusca || '').toLowerCase()) ||
                                      prod.sku?.toLowerCase().includes((item.produtoBusca || '').toLowerCase())
                                    ).length === 0 && (
                                      <div className="px-3 py-2 text-gray-500 text-sm">
                                        Nenhum produto encontrado
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                              {item.produto_id && (
                                <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                                  <p className="text-xs text-green-700 font-medium mb-1">Produto selecionado:</p>
                                  <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-xs text-gray-700">
                                    {item.codigo && (
                                      <div><span className="text-gray-500">SKU:</span> <span className="font-medium">{item.codigo}</span></div>
                                    )}
                                    <div><span className="text-gray-500">Nome:</span> <span className="font-medium">{item.descricao}</span></div>
                                    {item.modelo && (
                                      <div><span className="text-gray-500">Modelo:</span> <span className="font-medium">{item.modelo}</span></div>
                                    )}
                                    {item.categoria && (
                                      <div><span className="text-gray-500">Categoria:</span> <span className="font-medium">{item.categoria}</span></div>
                                    )}
                                    {item.marca && (
                                      <div><span className="text-gray-500">Marca:</span> <span className="font-medium">{item.marca}</span></div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Seleção de Tipo de Serviço - filtrado */}
                          {item.tipo === 'servico' && (
                            <div className="md:col-span-2">
                              <Label className="text-xs">Tipo de Serviço</Label>
                              <Select
                                value={item.tipo_servico_id || ''}
                                onValueChange={(v) => {
                                  const ts = tiposServico.find(t => t.id === v);
                                  if (ts) {
                                    onUpdateItem(index, 'tipo_servico_id', v);
                                    onUpdateItem(index, 'descricao', ts.nome);
                                    onUpdateItem(index, 'grupo', ts.grupo);
                                  }
                                }}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Selecionar..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {tiposServicoFiltrados
                                    .map(ts => (
                                      <SelectItem key={ts.id} value={ts.id}>
                                        [{ts.grupo || 'Geral'}] {ts.nome}
                                      </SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}

                          <div className={item.tipo === 'produto' || item.tipo === 'outro' ? '' : 'md:col-span-2'}>
                            <Label className="text-xs">Observação</Label>
                            <Input
                              value={item.observacao || ''}
                              onChange={(e) => onUpdateItem(index, 'observacao', e.target.value)}
                              placeholder="Informações adicionais para o fornecedor"
                              className="h-8 text-sm"
                            />
                          </div>

                          {/* Quantidade e Unidade - só para produto e outro */}
                          {(item.tipo === 'produto' || item.tipo === 'outro') && (
                            <div className="flex gap-2">
                              <div className="flex-1">
                                <Label className="text-xs">Qtd</Label>
                                <Input
                                  type="number"
                                  min="1"
                                  value={item.quantidade}
                                  onChange={(e) => onUpdateItem(index, 'quantidade', parseFloat(e.target.value) || 1)}
                                  className="h-8 text-sm"
                                />
                              </div>
                              <div className="w-20">
                                <Label className="text-xs">Un.</Label>
                                <Select
                                  value={item.unidade}
                                  onValueChange={(v) => onUpdateItem(index, 'unidade', v)}
                                >
                                  <SelectTrigger className="h-8">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="UN">UN</SelectItem>
                                    <SelectItem value="PC">PC</SelectItem>
                                    <SelectItem value="KG">KG</SelectItem>
                                    <SelectItem value="M">M</SelectItem>
                                    <SelectItem value="L">L</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          )}

                          {/* Valor Estimado - para todos os tipos */}
                          <div>
                            <Label className="text-xs">Valor Estimado (opcional)</Label>
                            <CurrencyInput
                              value={item.valor_estimado || 0}
                              onChange={(v) => onUpdateItem(index, 'valor_estimado', v)}
                              placeholder="0,00"
                              currency={cotacaoForm.moeda || 'BRL'}
                              className="h-8 text-sm"
                            />
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onRemoveItem(index)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                          data-testid={`btn-remove-item-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            <div className="flex justify-between pt-4 border-t">
              <Button variant="outline" onClick={onVoltar}>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Voltar
              </Button>
              <div className="flex gap-2">
                <span className="text-sm text-gray-500 self-center">
                  {cotacaoForm.itens.length} item(ns)
                </span>
                <Button onClick={onAvancar} data-testid="btn-avancar-step2">
                  Avançar
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Fornecedores */}
        {cotacaoStep === 3 && (
          <div className="space-y-4 mt-4">
            <div className="flex justify-between items-center">
              <Label className="text-base font-medium">Fornecedores para Cotação</Label>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onCadastrarFornecedor()}
                className="text-blue-600"
              >
                <Plus className="w-4 h-4 mr-1" />
                Cadastrar Fornecedor
              </Button>
            </div>

            {(() => {
              const selectedIds = new Set(cotacaoForm.fornecedores.map(f => f.fornecedor_id));
              const fornsSelecionados = fornecedores.filter(f => selectedIds.has(f.id));
              const termo = (fornSearchCot || '').trim().toLowerCase();
              const fornsBusca = !termo
                ? []
                : fornecedores
                    .filter(f => !selectedIds.has(f.id))
                    .filter(f => {
                      const nome = (getFornecedorNome(f) || '').toLowerCase();
                      const email = (f.email || '').toLowerCase();
                      const tel = (f.telefone || '').toLowerCase();
                      const cnpj = (f.cnpj || '').toLowerCase();
                      return nome.includes(termo) || email.includes(termo) || tel.includes(termo) || cnpj.includes(termo);
                    })
                    .slice(0, 12);
              return (
                <div className="space-y-3">
                  {/* Chips selecionados */}
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 min-h-[60px]" data-testid="fornecedores-selecionados-chips-edit">
                    <div className="text-xs text-slate-500 mb-1.5">
                      {fornsSelecionados.length === 0
                        ? 'Nenhum fornecedor selecionado ainda — busque abaixo para adicionar'
                        : `${fornsSelecionados.length} fornecedor(es) selecionado(s)`}
                    </div>
                    {fornsSelecionados.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {fornsSelecionados.map(forn => (
                          <span
                            key={forn.id}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-100 border border-blue-200 text-blue-800 text-xs"
                            data-testid={`chip-fornecedor-edit-${forn.id}`}
                          >
                            <Check className="w-3 h-3" />
                            <span className="max-w-[220px] truncate" title={getFornecedorNome(forn)}>{getFornecedorNome(forn)}</span>
                            <button
                              type="button"
                              onClick={() => onToggleFornecedor(forn)}
                              className="ml-0.5 hover:text-red-600"
                              title="Remover este fornecedor"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Busca */}
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={fornSearchCot}
                      onChange={(e) => setFornSearchCot(e.target.value)}
                      placeholder={`Buscar fornecedor por nome, e-mail, telefone ou CNPJ... (${fornecedores.length} cadastrados)`}
                      className="pl-9 pr-9"
                      data-testid="input-busca-fornecedor-edit"
                    />
                    {fornSearchCot && (
                      <button
                        type="button"
                        onClick={() => setFornSearchCot('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        title="Limpar busca"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  {/* Resultados */}
                  {termo && (
                    <div className="border rounded-lg bg-white max-h-[40vh] overflow-y-auto" data-testid="resultados-busca-edit">
                      {fornsBusca.length === 0 ? (
                        <div className="p-4 text-center text-sm text-slate-500">
                          Nenhum fornecedor disponível para &quot;{fornSearchCot}&quot;.
                          <button
                            type="button"
                            onClick={() => onCadastrarFornecedor()}
                            className="ml-2 text-blue-600 hover:underline"
                          >
                            Cadastrar agora?
                          </button>
                        </div>
                      ) : (
                        fornsBusca.map(forn => (
                          <button
                            type="button"
                            key={forn.id}
                            onClick={() => { onToggleFornecedor(forn); setFornSearchCot(''); }}
                            className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b last:border-b-0 transition-colors"
                            data-testid={`result-fornecedor-edit-${forn.id}`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <div className="font-medium text-sm truncate">{getFornecedorNome(forn)}</div>
                                <div className="text-[11px] text-slate-500 flex gap-2 truncate">
                                  {forn.email && <span>📧 {forn.email}</span>}
                                  {forn.telefone && <span>📞 {forn.telefone}</span>}
                                  {forn.cnpj && <span>🆔 {forn.cnpj}</span>}
                                </div>
                              </div>
                              <Plus className="w-4 h-4 text-blue-600 shrink-0" />
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {fornecedores.length === 0 && (
              <div className="text-center py-8 border-2 border-dashed rounded-lg">
                <Users className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500">Nenhum fornecedor cadastrado</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => onCadastrarFornecedor()}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Cadastrar Fornecedor
                </Button>
              </div>
            )}

            {/* Anexos da Cotação (Fase 2) - galeria diagramada */}
            <div className="border-t pt-4">
              <Label className="text-base font-medium block mb-2">Anexos da Cotação (galeria)</Label>
              {editingCotacao && cotacaoForm.id ? (
                <AnexosErrorBoundary>
                  <AnexosManager
                    anexos={(anexosCotacaoForm || []).map(a => ({
                      id: a.id,
                      nome: a.nome || a.filename,
                      nome_exibicao: a.nome_exibicao || (a.nome || a.filename || '').replace(/\.[^/.]+$/, ''),
                      descricao: a.descricao || '',
                      url: a.download_url || a.url,
                      tipo: a.tipo || a.content_type,
                      tamanho: a.tamanho || a.size,
                      data_upload: a.uploaded_at || a.data_upload,
                      uploaded_at: a.uploaded_at,
                      uploaded_by: a.uploaded_by,
                      s3_key: a.s3_key,
                      item_id: a.item_id || '',
                    }))}
                    onChange={() => { /* server-side managed */ }}
                    maxFiles={30}
                    maxSizeMB={50}
                    label="Fotos / Esquemas / PDFs do pedido"
                    modulo="compras_cotacoes"
                    entidadeId={cotacaoForm.id}
                    allowDescription
                    itensCotacao={(cotacaoForm.itens || []).map(it => ({ id: it.id, descricao: it.descricao }))}
                    uploadHandler={async (file, itemId) => {
                      await onUploadAnexo(cotacaoForm.id, file, itemId);
                      return null;
                    }}
                    deleteHandler={async (anexo) => {
                      await onDeleteAnexo(cotacaoForm.id, anexo.id);
                    }}
                    metadataUpdateHandler={async (anexo, patch) => {
                      await onPatchAnexoForm(cotacaoForm.id, anexo.id, patch);
                    }}
                    vincularItemHandler={async (anexo, item_id) => {
                      await onPatchAnexoForm(cotacaoForm.id, anexo.id, { item_id });
                    }}
                  />
                </AnexosErrorBoundary>
              ) : (
                /* Cotação NOVA — buffer local de anexos pendentes */
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-500">
                      Os arquivos abaixo serão enviados automaticamente após salvar a cotação.
                    </p>
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        multiple
                        className="hidden"
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          const novos = files.map(f => ({
                            file: f,
                            nome: f.name,
                            tamanho: f.size,
                            tipo: f.type,
                            item_id: null,
                            tempId: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                          }));
                          setAnexosPendentes(prev => [...prev, ...novos]);
                          e.target.value = '';
                        }}
                        data-testid="input-anexo-pendente"
                      />
                      <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-md bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200">
                        <Paperclip className="w-3 h-3" />
                        Adicionar arquivo(s)
                      </span>
                    </label>
                  </div>

                  {anexosPendentes.length === 0 ? (
                    <div className="text-center py-4 border-2 border-dashed rounded-lg bg-gray-50">
                      <Paperclip className="w-6 h-6 text-gray-300 mx-auto mb-1" />
                      <p className="text-xs text-gray-500">
                        Nenhum anexo adicionado ainda.
                      </p>
                    </div>
                  ) : (
                    <div className="border rounded-lg divide-y" data-testid="lista-anexos-pendentes">
                      {anexosPendentes.map((pend) => (
                        <div key={pend.tempId} className="flex items-center justify-between p-2 hover:bg-gray-50">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <Paperclip className="w-4 h-4 text-blue-500 shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm truncate">{pend.nome}</p>
                              <p className="text-[10px] text-gray-500">
                                {Math.round((pend.tamanho || 0) / 1024)} KB · {pend.tipo || 'arquivo'} · aguardando envio
                              </p>
                            </div>
                          </div>
                          {/* Vínculo com item (opcional) */}
                          {(cotacaoForm.itens || []).length > 0 && (
                            <select
                              value={pend.item_id || ''}
                              onChange={(e) => {
                                const v = e.target.value || null;
                                setAnexosPendentes(prev =>
                                  prev.map(p => p.tempId === pend.tempId ? { ...p, item_id: v } : p)
                                );
                              }}
                              className="text-[11px] border rounded px-1 py-0.5 mr-2 max-w-[180px]"
                              data-testid={`select-vinculo-pendente-${pend.tempId}`}
                            >
                              <option value="">Sem vínculo</option>
                              {cotacaoForm.itens.map(it => (
                                <option key={it.id} value={it.id}>
                                  {(it.descricao || '').slice(0, 30)}
                                </option>
                              ))}
                            </select>
                          )}
                          <button
                            type="button"
                            onClick={() => setAnexosPendentes(prev => prev.filter(p => p.tempId !== pend.tempId))}
                            className="text-red-500 hover:text-red-700 p-1"
                            title="Remover"
                            data-testid={`btn-remover-pendente-${pend.tempId}`}
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-between pt-4 border-t">
              <Button variant="outline" onClick={onVoltar}>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Voltar
              </Button>
              <div className="flex gap-2">
                <span className="text-sm text-gray-500 self-center">
                  {cotacaoForm.fornecedores.length} selecionado(s)
                </span>
                <Button
                  onClick={onSave}
                  disabled={submitLoading}
                  data-testid="btn-criar-cotacao"
                >
                  {submitLoading ? 'Salvando...' : editingCotacao ? 'Atualizar Cotação' : 'Criar Cotação'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CotacaoEditDialog;
